import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  CampoDeTexto,
  IconeCheck,
  IconeFechar,
  IconeLapis,
  IconeLixeira,
  Modal,
  Selo,
  Stepper,
  Tabela,
  ValorFiscal,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { baixarArquivo, requisicaoAutenticada } from '../../servicos/http';
import { lerComoBase64 } from '../../servicos/arquivo';
import { diaLocal } from '../../servicos/planilha';
import { ErroDaApi } from '../ErroDaApi';
import { EnvioDaSimulacaoPontual } from './EnvioDaSimulacaoPontual';
import { GerarTermo } from './GerarTermo';
import { Item, ROTULO_DO_DOCUMENTO, TOM_DA_ACAO, emBr, passosDa } from './comum';
import type {
  DocumentoPontual,
  LeituraDaRegra,
  ModelosDaNegociacao,
  Negociacao,
  ParcelaPontual,
} from './tipos';

/**
 * O ciclo de uma negociação, do fechamento ao último pagamento (RN-704).
 *
 * ## A tela segue o estado, não o contrário
 *
 * Os quatro passos aparecem sempre, na trilha do topo, com o que já aconteceu
 * datado. Embaixo dela vem **um** formulário: o do passo em que a negociação
 * está. Em vez de um painel com tudo desabilitado, o operador vê o que cabe
 * fazer agora e de quem é a bola — a frase vem pronta do servidor.
 *
 * ## Protocolar não é transferir
 *
 * São dois passos separados de propósito. Protocolar é pedir à Receita;
 * transferir é ela ter deferido, e é o certificado que prova isso. Só o segundo
 * gera parcela: contar prazo do protocolo cobraria o comprador por um crédito
 * que ainda está em análise.
 *
 * ## O combinado em texto, conferido antes de valer
 *
 * O campo de pagamento é uma frase, porque é assim que o acerto chega ("em até
 * 5 dias após a transferência"). O servidor lê e devolve o que entendeu; a tela
 * mostra a leitura e os vencimentos que sairiam dela **antes** de fechar.
 */
export function DetalheDaNegociacao(props: {
  readonly negociacaoId: string | null;
  readonly aoFechar: () => void;
  readonly aoMudar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar, pedirTexto } = useDialogo();
  const id = props.negociacaoId;

  const negociacao = useQuery({
    queryKey: ['pontual-negociacao', id],
    queryFn: () => requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}`),
    enabled: id !== null,
  });

  const [texto, setTexto] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [protocolo, setProtocolo] = useState('');
  const [requerimento, setRequerimento] = useState<File | null>(null);
  const [dataTransferencia, setDataTransferencia] = useState('');
  const [certificado, setCertificado] = useState<File | null>(null);
  const [contratoAssinado, setContratoAssinado] = useState<File | null>(null);
  /** O nome do último arquivo anexado — o aviso de que deu certo. */
  const [anexadoAgora, setAnexadoAgora] = useState<string | null>(null);
  const [termo, setTermo] = useState<File | null>(null);
  const [gerandoTermo, setGerandoTermo] = useState(false);
  /** O modelo escolhido para o contrato; `null` até mexer (vale o do servidor), `''` = esqueleto. */
  const [modeloDoContrato, setModeloDoContrato] = useState<string | null>(null);
  const [termoAnexado, setTermoAnexado] = useState<string | null>(null);

  const dados = negociacao.data;

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['pontual-negociacao', id] });
    props.aoMudar();
  };

  /** RN-700 — a leitura do combinado, feita enquanto se escreve. */
  const leitura = useQuery({
    queryKey: ['pontual-regra', texto, dados?.parcelas],
    queryFn: () =>
      requisicaoAutenticada<LeituraDaRegra>('/pontual/regra/leitura', {
        metodo: 'POST',
        corpo: {
          texto,
          parcelas: dados?.parcelas ?? 1,
          // Sem data ainda: a prévia dos vencimentos usa hoje só para ilustrar
          // o intervalo, e o cálculo de verdade sai do deferimento.
          dataTransferencia: new Date().toISOString().slice(0, 10),
        },
      }),
    enabled: id !== null && texto.trim().length > 2,
  });

  const fechar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/fechar`, {
        metodo: 'POST',
        corpo: {
          regraPagamentoTexto: texto,
          honorariosPagador: 'FORNECEDOR',
          ...(leitura.data?.reconhecida === true
            ? {
                regraPagamentoTipo: leitura.data.tipo,
                ...(leitura.data.dias === null ? {} : { regraPagamentoDias: leitura.data.dias }),
                regraPagamentoDiasUteis: leitura.data.diasUteis,
                ...(leitura.data.diaDoMes === null
                  ? {}
                  : { regraPagamentoDiaDoMes: leitura.data.diaDoMes }),
              }
            : {}),
        },
      }),
    onSuccess: invalidar,
  });

  /**
   * RN-705 — o contrato sai dos dados da negociação.
   *
   * Gerar e enviar são dois botões porque são duas coisas: a primeira é local e
   * repetível, a segunda cria documento no provedor de assinatura e dispara
   * e-mail para as partes.
   */
  /** D-020 — os modelos oferecidos: os gerais da aba Contratos e os das partes. */
  const modelos = useQuery({
    queryKey: ['pontual-modelos', id],
    queryFn: () =>
      requisicaoAutenticada<ModelosDaNegociacao>(`/pontual/negociacoes/${id ?? ''}/modelos`),
    enabled: id !== null,
  });
  const modeloEscolhido = modeloDoContrato ?? modelos.data?.contrato.escolhido ?? '';

  const gerarContrato = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/contrato`, {
        metodo: 'POST',
        corpo: modelos.data === undefined ? {} : { modeloId: modeloEscolhido || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pontual-modelos', id] });
      invalidar();
    },
  });

  const enviarContrato = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/contrato/envio`, {
        metodo: 'POST',
        corpo: {},
      }),
    onSuccess: invalidar,
  });

  /** A via assinada que voltou, guardada junto da negociação. */
  const anexarContrato = useMutation({
    mutationFn: async () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/documentos`, {
        metodo: 'POST',
        corpo: {
          tipo: 'CONTRATO',
          nome: contratoAssinado?.name ?? 'contrato-assinado.pdf',
          conteudoBase64: contratoAssinado === null ? '' : await lerComoBase64(contratoAssinado),
          ...(contratoAssinado === null || contratoAssinado.type === ''
            ? {}
            : { tipoConteudo: contratoAssinado.type }),
        },
      }),
    onSuccess: () => {
      setAnexadoAgora(contratoAssinado?.name ?? 'contrato-assinado.pdf');
      setContratoAssinado(null);
      invalidar();
    },
  });

  /**
   * O termo de comissionamento — quase toda negociação tem, nenhuma é obrigada
   * a ter. Não marca passo do ciclo: só fica guardado com ela.
   */
  const anexarTermo = useMutation({
    mutationFn: async (arquivo: File) =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/documentos`, {
        metodo: 'POST',
        corpo: {
          tipo: 'TERMO_COMISSIONAMENTO',
          nome: arquivo.name,
          conteudoBase64: await lerComoBase64(arquivo),
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
        },
      }),
    onSuccess: (_resposta, arquivo) => {
      setTermoAnexado(arquivo.name);
      setTermo(null);
      invalidar();
    },
  });

  /** O termo gerado vai ao D4Sign — só com o clique, nunca junto da geração. */
  const enviarTermo = useMutation({
    mutationFn: (documentoId: string) =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/termo/envio`, {
        metodo: 'POST',
        corpo: { documentoId },
      }),
    onSuccess: invalidar,
  });

  /** RN-704, passo 2 — o pedido na Receita. Não gera parcela nenhuma. */
  const protocolar = useMutation({
    mutationFn: async () => {
      if (requerimento !== null) {
        await requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/documentos`, {
          metodo: 'POST',
          corpo: {
            tipo: 'REQUERIMENTO',
            nome: requerimento.name,
            conteudoBase64: await lerComoBase64(requerimento),
            ...(requerimento.type === '' ? {} : { tipoConteudo: requerimento.type }),
          },
        });
      }
      return requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/protocolo`, {
        metodo: 'POST',
        corpo: { protocoloTransferencia: protocolo },
      });
    },
    onSuccess: () => {
      setRequerimento(null);
      invalidar();
    },
  });

  /** RN-704, passo 3 — o deferimento. É aqui que os vencimentos nascem. */
  const transferir = useMutation({
    mutationFn: async () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/transferencia`, {
        metodo: 'POST',
        corpo: {
          dataTransferencia,
          ...(certificado === null
            ? {}
            : {
                certificado: {
                  nome: certificado.name,
                  conteudoBase64: await lerComoBase64(certificado),
                  ...(certificado.type === '' ? {} : { tipoConteudo: certificado.type }),
                },
              }),
        },
      }),
    onSuccess: () => {
      setCertificado(null);
      invalidar();
    },
  });

  const removerDocumento = useMutation({
    mutationFn: (documentoId: string) =>
      requisicaoAutenticada<void>(`/pontual/documentos/${documentoId}`, { metodo: 'DELETE' }),
    onSuccess: invalidar,
  });

  const pagar = useMutation({
    mutationFn: (parcelaId: string) =>
      requisicaoAutenticada<Negociacao>(`/pontual/parcelas/${parcelaId}/pagamento`, {
        metodo: 'POST',
        corpo: {},
      }),
    onSuccess: invalidar,
  });

  /** A baixa grava o dia do clique; aqui se corrige para o dia em que o dinheiro entrou. */
  const corrigirBaixa = useMutation({
    mutationFn: (v: { readonly parcelaId: string; readonly dataPagamento: string }) =>
      requisicaoAutenticada<Negociacao>(`/pontual/parcelas/${v.parcelaId}/pagamento`, {
        metodo: 'PATCH',
        corpo: { dataPagamento: v.dataPagamento },
      }),
    onSuccess: invalidar,
  });

  const cancelar = useMutation({
    mutationFn: (motivo: string) =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${id ?? ''}/cancelar`, {
        metodo: 'POST',
        corpo: motivo === '' ? {} : { motivo },
      }),
    onSuccess: invalidar,
  });

  const temContrato = (dados?.documentos ?? []).some((d) => d.tipo === 'CONTRATO');

  // O check de que o contrato saiu (29/10/2026): o último gerado pelo Deca,
  // com a hora e o arquivo para baixar, ao lado dos botões. Logo depois de
  // gerar, diz "agora" — é a confirmação do clique.
  const ultimoContrato =
    [...(dados?.documentos ?? [])]
      .reverse()
      .find((d) => d.tipo === 'CONTRATO' && d.geradoPeloSistema) ?? null;
  const marcaDoContrato =
    ultimoContrato === null || gerarContrato.isPending ? null : (
      <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-conteudo-secundario">
        <IconeCheck className="h-4 w-4 text-economia" />
        {gerarContrato.isSuccess ? (
          <strong className="font-medium text-economia">Contrato gerado agora</strong>
        ) : (
          <span>
            Contrato gerado em {new Date(ultimoContrato.anexadoEm).toLocaleDateString('pt-BR')} às{' '}
            {new Date(ultimoContrato.anexadoEm).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
        ·
        <button
          type="button"
          onClick={() =>
            void baixarArquivo(`/pontual/documentos/${ultimoContrato.id}`, ultimoContrato.nome)
          }
          className="underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          Baixar
        </button>
      </span>
    );

  // O modelo do contrato, logo antes de "Gerar contrato" — nas duas etapas
  // em que o contrato se gera.
  const seletorDoContrato = (
    <CampoDeSelecao
      rotulo="Modelo do contrato"
      className="w-full sm:w-96"
      value={modeloEscolhido}
      onChange={(e) => setModeloDoContrato(e.target.value)}
      opcoes={[
        ...(modelos.data?.contrato.opcoes ?? []).map((m) => ({
          valor: m.id,
          rotulo: `${m.nome} — ${
            m.origem === 'GERAL'
              ? 'geral'
              : `${m.origem === 'FORNECEDOR' ? 'do fornecedor' : 'do assessor'}`
          }`,
        })),
        { valor: '', rotulo: 'Esqueleto padrão do Deca' },
      ]}
    />
  );

  const colunas: readonly ColunaTabela<ParcelaPontual>[] = [
    {
      chave: 'natureza',
      titulo: 'Quem paga',
      larguraMinima: '13rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-primario">
            {p.natureza === 'HONORARIOS_ASSESSOR' ? 'Honorários do assessor' : 'Comprador'}
          </span>
          <span className="text-xs text-conteudo-sutil">parcela {p.numeroParcela}</span>
        </span>
      ),
    },
    {
      chave: 'dataVencimento',
      titulo: 'Vencimento',
      larguraMinima: '8rem',
      celula: (p) => <span className="valor-fiscal text-sm">{emBr(p.dataVencimento)}</span>,
    },
    {
      chave: 'valor',
      titulo: 'Valor',
      numerica: true,
      larguraMinima: '9rem',
      celula: (p) => <ValorFiscal valor={p.valor} tamanho="pequeno" />,
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '8rem',
      celula: (p) => (
        <Selo
          tom={p.status === 'PAGA' ? 'positivo' : p.status === 'CANCELADA' ? 'neutro' : 'progresso'}
        >
          {p.status === 'PAGA' ? 'Paga' : p.status === 'CANCELADA' ? 'Cancelada' : 'Pendente'}
        </Selo>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '11rem',
      celula: (p) =>
        p.status === 'PAGA' && p.dataPagamento !== null ? (
          <DataDaBaixa
            dia={diaLocal(p.dataPagamento) ?? ''}
            salvando={corrigirBaixa.isPending && corrigirBaixa.variables.parcelaId === p.id}
            aoSalvar={(dataPagamento) => corrigirBaixa.mutate({ parcelaId: p.id, dataPagamento })}
          />
        ) : p.status === 'PENDENTE' || p.status === 'ATRASADA' ? (
          <Botao
            variante="secundario"
            tamanho="pequeno"
            carregando={pagar.isPending && pagar.variables === p.id}
            onClick={() => pagar.mutate(p.id)}
          >
            Dar baixa
          </Botao>
        ) : (
          <span className="text-xs text-conteudo-sutil">—</span>
        ),
    },
  ];

  return (
    <Modal
      aberto={id !== null}
      aoFechar={props.aoFechar}
      titulo="Negociação de crédito"
      descricao={
        dados === undefined
          ? undefined
          : `${dados.fornecedorNome ?? 'Fornecedor'} → ${dados.compradorNome ?? 'Comprador'}`
      }
      largura="larga"
      rodape={<Botao onClick={props.aoFechar}>Fechar</Botao>}
    >
      {negociacao.isError && (
        <ErroDaApi titulo="Não foi possível carregar" erro={negociacao.error} />
      )}
      {dados === undefined ? (
        <p className="py-8 text-center text-sm text-conteudo-sutil">Carregando…</p>
      ) : (
        <div className="flex flex-col gap-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Item rotulo="Face">
              <ValorFiscal valor={dados.valorFace} tamanho="pequeno" />
            </Item>
            <Item rotulo="Fornecedor recebe">
              <ValorFiscal valor={dados.valorCompra} tamanho="pequeno" />
            </Item>
            <Item rotulo="Comprador paga">
              <ValorFiscal valor={dados.valorVenda} tamanho="pequeno" />
            </Item>
            <Item rotulo="Spread">
              <ValorFiscal valor={dados.spread} variante="economia" tamanho="pequeno" />
            </Item>
          </dl>

          {/* A trilha dos quatro passos, com o que já aconteceu datado. */}
          <Stepper rotulo="Passos da negociação" passos={passosDa(dados)} />

          <Aviso
            tom={TOM_DA_ACAO[dados.proximaAcao.tom] ?? 'informacao'}
            titulo={dados.proximaAcao.resumo}
          >
            {dados.proximaAcao.detalhe}
          </Aviso>

          {/*
            O termo de comissionamento vem antes do passo 1: é combinado junto
            com a negociação, mas não trava passo nenhum. Gerar pelo Deca ainda
            não existe — o botão fica no lugar em que vai funcionar.
          */}
          {dados.status !== 'CANCELADA' && (
            <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-conteudo-primario">
                  Termo de comissionamento
                </h3>
                <span className="text-xs text-conteudo-sutil">Opcional</span>
              </div>
              <p className="text-xs text-conteudo-secundario">
                O acordo da comissão desta negociação. Gere pelo Deca ou anexe o termo feito fora
                dele (PDF ou imagem) — fica guardado aqui, com os outros documentos.
              </p>
              <CampoDeArquivo
                rotuloAcessivel="Arquivo do termo de comissionamento"
                antes={
                  <>
                    <Botao
                      variante="secundario"
                      disabled={modelos.data === undefined}
                      onClick={() => setGerandoTermo(true)}
                    >
                      Gerar termo
                    </Botao>
                    <span className="text-xs text-conteudo-sutil">ou</span>
                  </>
                }
                arquivo={termo}
                aoEscolher={(arquivo) => {
                  setTermoAnexado(null);
                  setTermo(arquivo);
                }}
                anexados={dados.documentos.filter((d) => d.tipo === 'TERMO_COMISSIONAMENTO')}
                acaoDoAnexado={(d) =>
                  !d.geradoPeloSistema ? null : d.enviadoParaAssinatura ? (
                    <span className="shrink-0 text-xs text-conteudo-secundario">
                      · enviado para assinatura
                    </span>
                  ) : (
                    <Botao
                      variante="sutil"
                      tamanho="pequeno"
                      carregando={enviarTermo.isPending && enviarTermo.variables === d.id}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirmar({
                            titulo: 'Enviar o termo para assinatura?',
                            descricao:
                              'O termo vai para o D4Sign, que envia e-mail para quem assina pelo fornecedor e pelo comissionado (o assessor).',
                            rotuloConfirmar: 'Enviar',
                          });
                          if (ok) enviarTermo.mutate(d.id);
                        })();
                      }}
                    >
                      Enviar para assinatura
                    </Botao>
                  )
                }
                acao={
                  <Botao
                    variante="secundario"
                    disabled={termo === null}
                    carregando={anexarTermo.isPending}
                    onClick={() => {
                      if (termo !== null) anexarTermo.mutate(termo);
                    }}
                  >
                    Anexar
                  </Botao>
                }
              />
              {termoAnexado !== null && (
                <Aviso tom="positivo" titulo="Termo anexado">
                  {termoAnexado} foi guardado na negociação.
                </Aviso>
              )}
              {anexarTermo.isError && (
                <ErroDaApi titulo="Não foi possível anexar" erro={anexarTermo.error} />
              )}
              {enviarTermo.isError && (
                <ErroDaApi titulo="Não foi possível enviar o termo" erro={enviarTermo.error} />
              )}
            </section>
          )}

          {/*
            Passo 1 num cartão só: o combinado de pagamento, o contrato que sai
            dele e o fechamento. Fechar **é** registrar o contrato assinado —
            dois botões para o mesmo ato fariam a operação perguntar qual deles
            vale.
          */}
          {dados.status === 'SIMULACAO' && (
            <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-conteudo-primario">
                  Passo 1 · Negociação e contrato
                </h3>
                <Botao variante="sutil" tamanho="pequeno" onClick={() => setEnviandoEmail(true)}>
                  Enviar simulação por e-mail
                </Botao>
              </div>
              <CampoDeTexto
                rotulo="Quando o comprador paga"
                rows={2}
                ajuda='Escreva como foi combinado: "em até 5 dias após a transferência", "a partir do dia 25 do mês subsequente", "à vista".'
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />

              {leitura.data !== undefined && texto.trim().length > 2 && (
                <Aviso
                  tom={
                    !leitura.data.reconhecida
                      ? 'atencao'
                      : leitura.data.confianca === 'baixa'
                        ? 'atencao'
                        : 'positivo'
                  }
                  titulo={leitura.data.reconhecida ? 'Entendi assim' : 'Não entendi o combinado'}
                >
                  {leitura.data.reconhecida ? (
                    <>
                      <p>{leitura.data.descricao}</p>
                      {leitura.data.confianca === 'baixa' && (
                        <p className="mt-1 text-xs">
                          A frase não diz de que evento conta o prazo — confira antes de fechar.
                        </p>
                      )}
                      {leitura.data.vencimentos.length > 0 && (
                        <p className="mt-1 text-xs text-conteudo-secundario">
                          Se o deferimento saísse hoje, venceria em{' '}
                          {leitura.data.vencimentos.map(emBr).join(', ')}.
                        </p>
                      )}
                    </>
                  ) : (
                    <p>
                      Reescreva em uma das formas conhecidas — "à vista", "em até N dias após a
                      transferência", "dia N do mês subsequente".
                    </p>
                  )}
                </Aviso>
              )}

              <div className="flex flex-col gap-2 border-t border-borda pt-3">
                <p className="text-xs text-conteudo-secundario">
                  O contrato sai destes dados — partes, valores, deságios e o combinado de pagamento
                  —, no modelo escolhido aqui.
                </p>
                {seletorDoContrato}
                <div className="flex flex-wrap items-center gap-2">
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    carregando={gerarContrato.isPending}
                    onClick={() => gerarContrato.mutate()}
                  >
                    {temContrato ? 'Gerar de novo' : 'Gerar contrato'}
                  </Botao>
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    disabled={!temContrato}
                    carregando={enviarContrato.isPending}
                    onClick={() => {
                      void (async () => {
                        const ok = await confirmar({
                          titulo: 'Enviar o contrato para assinatura?',
                          descricao:
                            'O documento vai para o provedor de assinatura eletrônica, que envia e-mail para quem assina por cada empresa.',
                          rotuloConfirmar: 'Enviar',
                        });
                        if (ok) enviarContrato.mutate();
                      })();
                    }}
                  >
                    Enviar para assinatura
                  </Botao>
                  {marcaDoContrato}
                </div>
                {dados.contratoEnviadoEm !== null && (
                  <p className="text-xs text-conteudo-secundario">
                    Enviado para assinatura em{' '}
                    {new Date(dados.contratoEnviadoEm).toLocaleDateString('pt-BR')}.
                  </p>
                )}
                {(gerarContrato.isError || enviarContrato.isError) && (
                  <ErroDaApi
                    titulo="Não foi possível"
                    erro={gerarContrato.error ?? enviarContrato.error}
                  />
                )}
              </div>

              {fechar.isError && <ErroDaApi titulo="Não foi possível fechar" erro={fechar.error} />}

              <div className="flex flex-wrap items-center gap-2 border-t border-borda pt-3">
                <Botao
                  disabled={leitura.data?.reconhecida !== true}
                  carregando={fechar.isPending}
                  onClick={() => fechar.mutate()}
                >
                  Fechar com o contrato assinado
                </Botao>
                <Botao
                  variante="sutil"
                  onClick={() => {
                    void (async () => {
                      const motivo = await pedirTexto({
                        titulo: 'Cancelar esta negociação?',
                        descricao:
                          'A linha fica registrada como cancelada, com o motivo — nada é apagado.',
                        rotulo: 'Motivo (opcional)',
                        multilinha: true,
                        rotuloConfirmar: 'Cancelar negociação',
                      });
                      if (motivo !== null) cancelar.mutate(motivo);
                    })();
                  }}
                >
                  Cancelar negociação
                </Botao>
              </div>
            </section>
          )}

          {/*
            Depois de fechada, o contrato vira arquivo: baixar o gerado, gerar
            de novo se algum dado mudou, ou guardar a via assinada que voltou.
            Não há botão de "assinar": isso foi o fechamento.
          */}
          {dados.status === 'FECHADA' && (
            <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
              <h3 className="text-sm font-semibold text-conteudo-primario">Contrato de cessão</h3>
              <p className="text-xs text-conteudo-secundario">
                Fechada em {emBr(dados.contratoAssinadoEm ?? '')} com o contrato assinado. Guarde
                aqui a via assinada, se ela voltou em arquivo.
              </p>

              {seletorDoContrato}
              <div className="flex flex-wrap items-center gap-2">
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  carregando={gerarContrato.isPending}
                  onClick={() => gerarContrato.mutate()}
                >
                  {temContrato ? 'Gerar de novo' : 'Gerar contrato'}
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  disabled={!temContrato}
                  carregando={enviarContrato.isPending}
                  onClick={() => {
                    void (async () => {
                      const ok = await confirmar({
                        titulo: 'Enviar o contrato para assinatura?',
                        descricao:
                          'O documento vai para o provedor de assinatura eletrônica, que envia e-mail para quem assina por cada empresa.',
                        rotuloConfirmar: 'Enviar',
                      });
                      if (ok) enviarContrato.mutate();
                    })();
                  }}
                >
                  Enviar para assinatura
                </Botao>
                {marcaDoContrato}
              </div>

              <CampoDeArquivo
                rotulo="Via assinada (opcional)"
                ajuda="O contrato com as assinaturas das partes."
                arquivo={contratoAssinado}
                aoEscolher={(arquivo) => {
                  setAnexadoAgora(null);
                  setContratoAssinado(arquivo);
                }}
                anexados={dados.documentos.filter((d) => d.tipo === 'CONTRATO')}
                acao={
                  <Botao
                    variante="secundario"
                    disabled={contratoAssinado === null}
                    carregando={anexarContrato.isPending}
                    onClick={() => anexarContrato.mutate()}
                  >
                    Anexar
                  </Botao>
                }
              />
              {anexadoAgora !== null && (
                <Aviso tom="positivo" titulo="Arquivo anexado">
                  {anexadoAgora} foi guardado na negociação.
                </Aviso>
              )}

              {(gerarContrato.isError || enviarContrato.isError || anexarContrato.isError) && (
                <ErroDaApi
                  titulo="Não foi possível"
                  erro={gerarContrato.error ?? enviarContrato.error ?? anexarContrato.error}
                />
              )}
            </section>
          )}

          {/* Passo 2 — requerimento assinado e protocolo na Receita. */}
          {dados.status === 'FECHADA' && (
            <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Passo 2 · Requerimento e protocolo
              </h3>
              <p className="text-xs text-conteudo-secundario">
                O requerimento de transferência assinado é o que se protocola na Receita do Paraná.
                Com o número do protocolo, este passo está cumprido — o pagamento ainda não começa a
                contar, quem conta é o deferimento.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  rotulo="Protocolo (e-Protocolo)"
                  ajuda="O número que a Receita devolveu ao receber o pedido."
                  value={protocolo}
                  onChange={(e) => setProtocolo(e.target.value)}
                />
                <CampoDeArquivo
                  rotulo="Requerimento assinado (opcional)"
                  ajuda="Guarda o papel junto da negociação."
                  arquivo={requerimento}
                  aoEscolher={setRequerimento}
                  anexados={dados.documentos.filter((d) => d.tipo === 'REQUERIMENTO')}
                />
              </div>

              {protocolar.isError && (
                <ErroDaApi titulo="Não foi possível protocolar" erro={protocolar.error} />
              )}

              <div>
                <Botao
                  disabled={protocolo.trim() === ''}
                  carregando={protocolar.isPending}
                  onClick={() => protocolar.mutate()}
                >
                  Registrar protocolo
                </Botao>
              </div>
            </section>
          )}

          {/* Passo 3 — o deferimento da Receita, que dá data ao dinheiro. */}
          {dados.status === 'PROTOCOLADA' && (
            <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Passo 3 · Transferência deferida
              </h3>
              <p className="text-xs text-conteudo-secundario">
                Protocolo{' '}
                <span className="valor-fiscal">{dados.protocoloTransferencia ?? '—'}</span> em
                análise. Quando o certificado chegar, anexe-o com a data do deferimento: é dela que{' '}
                {dados.regraPagamentoDescricao ?? 'os vencimentos'} contam.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <CampoDeArquivo
                  rotulo="Certificado de transferência"
                  ajuda="O documento deferido pela Receita."
                  arquivo={certificado}
                  aoEscolher={setCertificado}
                  anexados={dados.documentos.filter((d) => d.tipo === 'CERTIFICADO')}
                />
                <CampoDeData
                  rotulo="Deferido em"
                  ajuda="A data que consta no certificado."
                  value={dataTransferencia}
                  onChange={setDataTransferencia}
                />
              </div>

              {transferir.isError && (
                <ErroDaApi titulo="Não foi possível confirmar" erro={transferir.error} />
              )}

              <div>
                <Botao
                  disabled={dataTransferencia === ''}
                  carregando={transferir.isPending}
                  onClick={() => transferir.mutate()}
                >
                  Confirmar transferência e gerar parcelas
                </Botao>
              </div>
            </section>
          )}

          {dados.documentos.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Documentos ({dados.documentos.length})
              </h3>
              <ul className="flex flex-col divide-y divide-borda rounded-card border border-borda">
                {dados.documentos.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex min-w-0 flex-col">
                      <button
                        type="button"
                        onClick={() => void baixarArquivo(`/pontual/documentos/${d.id}`, d.nome)}
                        className="truncate text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
                      >
                        {d.nome}
                      </button>
                      <span className="text-xs text-conteudo-sutil">
                        {ROTULO_DO_DOCUMENTO[d.tipo]}
                        {d.dataDoDocumento !== null && ` · ${emBr(d.dataDoDocumento)}`}
                      </span>
                    </span>
                    <button
                      type="button"
                      aria-label={`Remover ${d.nome}`}
                      title="Remover documento"
                      className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-erro/10 hover:text-erro"
                      onClick={() => {
                        void (async () => {
                          const ok = await confirmar({
                            titulo: 'Remover este documento?',
                            descricao:
                              'A referência sai da negociação. O que ele já marcou — protocolo, deferimento, parcelas — continua valendo.',
                            rotuloConfirmar: 'Remover',
                            tom: 'perigo',
                          });
                          if (ok) removerDocumento.mutate(d.id);
                        })();
                      }}
                    >
                      <IconeLixeira className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
              {removerDocumento.isError && (
                <ErroDaApi titulo="Não foi possível remover" erro={removerDocumento.error} />
              )}
            </section>
          )}

          {dados.parcelasPagamento.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Passo 4 · Pagamentos ({dados.parcelasPagamento.length})
              </h3>
              <Tabela
                legenda="Parcelas da negociação"
                densidade="compacta"
                colunas={colunas}
                dados={[...dados.parcelasPagamento]}
                chaveDaLinha={(p) => p.id}
                chaveDeCabecalhoDaLinha="natureza"
              />
              {pagar.isError && (
                <ErroDaApi titulo="Não foi possível dar baixa" erro={pagar.error} />
              )}
              {corrigirBaixa.isError && (
                <ErroDaApi titulo="Não foi possível corrigir a data" erro={corrigirBaixa.error} />
              )}
            </section>
          )}

          {dados.status !== 'SIMULACAO' && dados.status !== 'CANCELADA' && (
            <div className="flex justify-end">
              <Botao
                variante="sutil"
                tamanho="pequeno"
                onClick={() => {
                  void (async () => {
                    const ok = await confirmar({
                      titulo: 'Cancelar esta negociação?',
                      descricao:
                        'As parcelas pendentes são canceladas junto. O registro fica, com o histórico.',
                      rotuloConfirmar: 'Cancelar negociação',
                      tom: 'perigo',
                    });
                    if (ok) cancelar.mutate('');
                  })();
                }}
              >
                Cancelar negociação
              </Botao>
            </div>
          )}
        </div>
      )}
      {gerandoTermo && dados !== undefined && (
        <GerarTermo
          negociacao={dados}
          modelos={modelos.data?.termo.opcoes ?? []}
          aoFechar={() => setGerandoTermo(false)}
          aoGerar={() => {
            setGerandoTermo(false);
            invalidar();
          }}
        />
      )}
      {enviandoEmail && dados !== undefined && (
        <EnvioDaSimulacaoPontual
          parametros={{
            valorFace: dados.valorFace,
            desagioCompraPercentual: dados.desagioCompraPercentual,
            desagioVendaPercentual: dados.desagioVendaPercentual,
            parcelas: dados.parcelas,
          }}
          compradorId={dados.compradorId}
          fornecedorId={dados.fornecedorId}
          aoFechar={() => setEnviandoEmail(false)}
        />
      )}
    </Modal>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

/**
 * "Baixa em 25/10/2026", com o lápis para corrigir. A baixa grava o dia do
 * clique; quando o dinheiro entrou noutro dia, corrige-se ali mesmo, sem
 * desfazer a baixa.
 */
function DataDaBaixa(props: {
  readonly dia: string;
  readonly salvando: boolean;
  readonly aoSalvar: (dia: string) => void;
}): JSX.Element {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(props.dia);

  if (!editando) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-conteudo-secundario">
        Baixa em <span className="valor-fiscal">{emBr(props.dia)}</span>
        <button
          type="button"
          aria-label="Corrigir a data da baixa"
          title="Corrigir a data da baixa"
          className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-superficie-elevada hover:text-conteudo-primario"
          onClick={() => {
            setValor(props.dia);
            setEditando(true);
          }}
        >
          <IconeLapis className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  const salvar = (): void => {
    if (valor !== '' && valor !== props.dia) props.aoSalvar(valor);
    setEditando(false);
  };
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="date"
        aria-label="Data da baixa"
        autoFocus
        value={valor}
        max={new Date().toLocaleDateString('sv-SE')}
        disabled={props.salvando}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') salvar();
          if (e.key === 'Escape') setEditando(false);
        }}
        className="h-8 rounded-card border border-borda bg-superficie-base px-2 text-xs text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      />
      <button
        type="button"
        aria-label="Salvar a data"
        title="Salvar"
        disabled={valor === ''}
        className="rounded-card p-1 text-economia transition-colors hover:bg-economia/10 disabled:opacity-40"
        onClick={salvar}
      >
        <IconeCheck className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Cancelar"
        title="Cancelar"
        className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-superficie-elevada hover:text-conteudo-primario"
        onClick={() => setEditando(false)}
      >
        <IconeFechar className="h-4 w-4" />
      </button>
    </span>
  );
}

function CampoDeData(props: {
  readonly rotulo: string;
  readonly ajuda?: string;
  readonly value: string;
  readonly onChange: (valor: string) => void;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm font-medium text-conteudo-primario">{props.rotulo}</span>
      <input
        type="date"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="h-11 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      />
      {props.ajuda !== undefined && (
        <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
      )}
    </label>
  );
}

/**
 * Escolher um arquivo — e, logo embaixo, os que já foram anexados daquele tipo,
 * clicáveis como na lista de documentos.
 *
 * `antes` entra na mesma linha, à esquerda do "Escolher arquivo" (o "Gerar
 * termo", no termo de comissionamento); sem `rotulo`, o campo se nomeia por
 * `rotuloAcessivel`.
 *
 * O botão de anexar (`acao`) fica na mesma linha do "Escolher arquivo": numa
 * coluna à parte ele ficava desalinhado quando o texto de ajuda some depois da
 * escolha. A escolha volta ao vazio quando `arquivo` volta a `null` (anexado),
 * para o nome do arquivo já guardado não continuar no campo como se faltasse
 * enviar.
 */
function CampoDeArquivo(props: {
  readonly rotulo?: string;
  readonly rotuloAcessivel?: string;
  readonly antes?: React.ReactNode;
  readonly ajuda?: string;
  readonly arquivo: File | null;
  readonly aoEscolher: (arquivo: File | null) => void;
  readonly anexados?: readonly DocumentoPontual[];
  readonly acao?: React.ReactNode;
  /** Ao lado de cada anexado — o "Enviar para assinatura" do termo gerado. */
  readonly acaoDoAnexado?: (documento: DocumentoPontual) => React.ReactNode;
}): JSX.Element {
  const anexados = props.anexados ?? [];
  const campo = useRef<HTMLInputElement>(null);
  const idDoCampo = useId();
  // Anexado (a escolha voltou a vazio): o campo nativo esquece o arquivo.
  useEffect(() => {
    if (props.arquivo === null && campo.current !== null) campo.current.value = '';
  }, [props.arquivo]);
  return (
    <div className="flex flex-col gap-2">
      {props.rotulo !== undefined && (
        <label htmlFor={idDoCampo} className="text-sm font-medium text-conteudo-primario">
          {props.rotulo}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {props.antes}
        <input
          ref={campo}
          id={idDoCampo}
          aria-label={props.rotulo === undefined ? props.rotuloAcessivel : undefined}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          onChange={(e) => props.aoEscolher(e.target.files?.[0] ?? null)}
          className="min-w-0 max-w-full text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
        />
        {props.acao}
      </div>
      {props.arquivo === null && props.ajuda !== undefined && (
        <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
      )}
      {anexados.length > 0 && (
        <ul className="flex flex-col gap-1">
          {anexados.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-sm">
              <span aria-hidden="true" className="text-economia">
                ✓
              </span>
              <button
                type="button"
                onClick={() => void baixarArquivo(`/pontual/documentos/${d.id}`, d.nome)}
                className="truncate text-left text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
              >
                {d.nome}
              </button>
              <span className="shrink-0 text-xs text-conteudo-sutil">
                {d.geradoPeloSistema ? 'gerado' : 'anexado'} em{' '}
                {new Date(d.anexadoEm).toLocaleDateString('pt-BR')}
              </span>
              {props.acaoDoAnexado?.(d)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
